import React, { useState, useEffect } from "react";
import baseURL from "../../authComponents/baseURL/BaseURL";
import loginToken from "../../authComponents/localStorageItems/LoginTokin";

const DataCard = ({ icon, title, value, change }) => (
  <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-between font-serif">
    <div className="flex items-center mb-4">
      <div className="bg-gray-100 rounded-full p- w-10 h-10 mr-4 flex justify-center items-center font">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-600 uppercase font-serif">
        {title}
      </h3>
    </div>
    <div>
      <p className="text-3xl font-bold text-gray-800 font-serif">{value}</p>
      {change && (
        <p
          className={`text-sm mt-1 font-serif ${
            change && change.includes("+") ? "text-green-500" : "text-[#730000]"
          }`}
        >
          {change}
        </p>
      )}
    </div>
    <hr className="mt-4 border-blue-500" />
  </div>
);

const Dashboard = () => {
  // Initialize state with loading values
  const [stats, setStats] = useState([
    {
      title: "Total Users",
      value: "Loading...",
      change: "",
      icon: <i className="fas fa-users text-blue-500 text-lg"></i>,
    },
    {
      title: "Verified Users",
      value: "Loading...",
      change: "",
      icon: <i className="fas fa-check-circle text-green-500 text-lg"></i>,
    },
    {
      title: "Pending Users",
      value: "Loading...",
      change: "",
      icon: <i className="fas fa-hourglass text-yellow-500 text-lg"></i>,
    },
    {
      title: "Suspended Users",
      value: "Loading...",
      change: "",
      icon: <i className="fas fa-ban text-red-500 text-lg"></i>,
    },
    {
      title: "Deleted Users",
      value: "Loading...",
      change: "",
      icon: <i className="fas fa-trash text-gray-500 text-lg"></i>,
    },
  ]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch real data from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await baseURL.get("/admin/users_counts", {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        });

        // Extract data from API response
        const data = response.data.data;

        // Check if data exists and has the expected structure
        if (data) {
          // Helper function to generate placeholder changes
          // In a real application, this would come from the API
          const calculateChange = (value) => {
            // This is just a placeholder - you'd want real change data from your API
            // For now, returning an empty string since the implementation is incomplete
            return "";
          };

          const totalChange = calculateChange(data.total_users);
          const activeChange = calculateChange(data.active_users);
          const pendingChange = calculateChange(data.pending_users);
          const suspendedChange = calculateChange(data.suspended_users);
          const deletedChange = calculateChange(data.deleted_users);

          setStats([
            {
              title: "Total Users",
              value: data.total_users || 0,
              change: totalChange,
              icon: <i className="fas fa-users text-blue-500 text-lg"></i>,
            },
            {
              title: "Active Users",
              value: data.active_users || 0,
              change: activeChange,
              icon: (
                <i className="fas fa-check-circle text-green-500 text-lg"></i>
              ),
            },
            {
              title: "Pending Users",
              value: data.pending_users || 0,
              change: pendingChange,
              icon: (
                <i className="fas fa-hourglass text-yellow-500 text-lg"></i>
              ),
            },
            {
              title: "Suspended Users",
              value: data.suspended_users || 0,
              change: suspendedChange,
              icon: <i className="fas fa-ban text-red-500 text-lg"></i>,
            },
            {
              title: "Deleted Users",
              value: data.deleted_users || 0,
              change: deletedChange,
              icon: <i className="fas fa-trash text-gray-500 text-lg"></i>,
            },
          ]);
        } else {
          throw new Error("Invalid data structure received from API");
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setError("Failed to load dashboard data. Please try again later.");

        // Set error state in the cards
        setStats((prevStats) =>
          prevStats.map((stat) => ({
            ...stat,
            value: "Error",
            change: "Failed to load data",
          }))
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-8 font-serif">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 font-serif">
          Admin Dashboard
        </h1>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 font-serif">
        {stats.map((stat, index) => (
          <DataCard
            key={index}
            icon={stat.icon}
            title={stat.title}
            value={stat.value}
            change={stat.change}
          />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
